import { useState, useEffect, Suspense } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import PageLoadingSpinner from './PageLoadingSpinner'
import { Home, FileText, Shield, FolderOpen, Building2, LogOut, AlertCircle, Settings, MessageSquare } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useProfile } from '../hooks/useProfile'
import { getMyUnreadMessageCount } from '../services/tenancyMessages'
import Logo from './Logo'
import NotificationsDropdown from './NotificationsDropdown'
import TermsAcceptanceModal, { useTermsAccepted } from './TermsAcceptanceModal'

// Navigation configuration with emoji icons for desktop sidebar
const NAV_ITEMS = {
  main: [
    { to: '/portal', icon: '🏠', lucideIcon: Home, label: 'Home', end: true },
    { to: '/portal/tenancy', icon: '📋', lucideIcon: FileText, label: 'Tenancy' },
    { to: '/portal/deposit', icon: '🛡️', lucideIcon: Shield, label: 'Deposit' },
    { to: '/portal/documents', icon: '📁', lucideIcon: FolderOpen, label: 'Documents' },
    { to: '/portal/property', icon: '🏢', lucideIcon: Building2, label: 'Property' },
    { to: '/my-issues', icon: '🔧', lucideIcon: AlertCircle, label: 'Issues' },
    { to: '/portal/messages', icon: '✉️', lucideIcon: MessageSquare, label: 'Messages' },
  ],
  mobile: [
    { to: '/portal', icon: Home, label: 'Home', end: true },
    { to: '/portal/tenancy', icon: FileText, label: 'Tenancy' },
    { to: '/portal/deposit', icon: Shield, label: 'Deposit' },
    { to: '/portal/documents', icon: FolderOpen, label: 'Docs' },
    { to: '/portal/property', icon: Building2, label: 'Property' },
    { to: '/my-issues', icon: AlertCircle, label: 'Issues' },
    { to: '/portal/messages', icon: MessageSquare, label: 'Messages' },
  ],
}

export default function TenantLayout() {
  const { user, signOut } = useAuth()
  const { profile } = useProfile()
  const location = useLocation()
  const [termsAccepted, setTermsAccepted] = useState(false)
  const termsStatus = useTermsAccepted(user?.id)
  const [unreadMessages, setUnreadMessages] = useState(0)

  useEffect(() => {
    getMyUnreadMessageCount().then(setUnreadMessages).catch(() => {})
  }, [location.pathname])

  const firstName = profile?.full_name?.split(' ')[0] || 'there'

  // Show modal only when DB check confirms terms not accepted (termsStatus === false)
  // Don't show while loading (termsStatus === null) to avoid flash
  const showTermsModal = termsStatus === false && !termsAccepted

  const handleTermsAccept = () => {
    setTermsAccepted(true)
  }

  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good Morning'
    if (hour < 17) return 'Good Afternoon'
    return 'Good Evening'
  }

  return (
    <>
      {showTermsModal && <TermsAcceptanceModal userType="tenant" onAccept={handleTermsAccept} />}
    <div className="dashboard-layout">
      {/* Desktop Sidebar - Navy */}
      <aside className="hidden md:flex fixed top-0 left-0 bottom-0 w-64 flex-col z-30"
        style={{ background: 'var(--bg-sidebar)' }}>
        {/* Logo */}
        <div className="px-6 py-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <Logo size="md" variant="light" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <p className="px-3 mb-2 text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Tenant Portal
          </p>
          <div className="space-y-1">
            {NAV_ITEMS.main.map(({ to, icon, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `sidebar-nav-link group flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] transition-all duration-150 ${
                    isActive ? 'sidebar-nav-link-active' : ''
                  }`
                }
              >
                <span className="text-base w-5 text-center transition-transform duration-150 group-hover:scale-110">{icon}</span>
                <span className="flex-1">{label}</span>
                {to === '/portal/messages' && unreadMessages > 0 && (
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--color-accent)' }} />
                )}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Footer */}
        <div className="px-3 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          {/* Support is email-only: the in-app ticket feature was deleted
              (0 rows, pre-launch) and replaced with a mailto. */}
          <a
            href="mailto:support@lentals.com"
            className="sidebar-nav-link group flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] transition-all duration-150"
          >
            <span className="text-base w-5 text-center">💬</span>
            Contact support
          </a>
          <NavLink
            to="/portal/settings"
            className={({ isActive }) =>
              `sidebar-nav-link group flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] transition-all duration-150 ${
                isActive ? 'sidebar-nav-link-active' : ''
              }`
            }
          >
            <span className="text-base w-5 text-center">⚙️</span>
            Settings
          </NavLink>
          <button
            onClick={signOut}
            className="sidebar-nav-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] w-full transition-all duration-150"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 md:ml-64 overflow-auto min-h-screen pb-20 md:pb-0" style={{ background: 'var(--bg-surface)' }}>
        {/* Mobile Header */}
        <header className="md:hidden sticky top-0 z-20 px-4 py-3" style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--border-color)' }}>
          <div className="flex items-center justify-between">
            <Logo size="sm" variant="dark" />
            <div className="flex items-center gap-2">
              <NotificationsDropdown />
              <NavLink
                to="/portal/settings"
                className="p-2 rounded-lg transition"
                style={{ color: 'var(--text-secondary)' }}
              >
                <Settings className="w-5 h-5" />
              </NavLink>
            </div>
          </div>
        </header>

        {/* Desktop Header */}
        <header className="hidden md:block sticky top-0 z-20" style={{
          background: 'var(--bg-card)',
          borderBottom: '1px solid var(--border-color)',
          padding: '20px 32px',
        }}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {getGreeting()}, {firstName}
              </h1>
              <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                Welcome to your tenant portal
              </p>
            </div>
            <div className="flex items-center gap-3">
              <NotificationsDropdown />
              <svg viewBox="0 0 120 120" className="w-10 h-10 rounded-xl">
                <rect width="120" height="120" rx="26" fill="#1a2744"/>
                <path d="M36 30V86H84" stroke="#ffffff" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <circle cx="84" cy="86" r="12" fill="#c87941"/>
              </svg>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="p-4 md:p-8 2xl:!max-w-[1600px]" style={{ maxWidth: 1400, width: '100%', margin: '0 auto' }}>
          <Suspense fallback={<PageLoadingSpinner />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-20 md:hidden safe-bottom"
        style={{
          backgroundColor: 'var(--bg-card)',
          borderTop: '1px solid var(--border-color)'
        }}
      >
        <div className="flex">
          {NAV_ITEMS.mobile.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className="flex-1 flex flex-col items-center py-2.5 text-xs transition min-h-[52px] relative"
              style={({ isActive }) => ({
                color: isActive ? 'var(--color-accent)' : 'var(--text-secondary)',
                backgroundColor: isActive ? 'var(--color-accent-muted)' : 'transparent',
                fontWeight: isActive ? 600 : 400
              })}
            >
              <Icon className="w-5 h-5 mb-0.5" />
              {label}
              {to === '/portal/messages' && unreadMessages > 0 && (
                <span className="absolute top-1.5 right-1/4 w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-accent)' }} />
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
    </>
  )
}
